import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Pin } from "lucide-react";
import { useContentList, useContentObject } from "../hooks/useContentData";
import { contentFallback } from "../contentDefaults";

const News = ({ limit = 4 }) => {
  const { data: header } = useContentObject("home.news_header", contentFallback("home.news_header"));
  const { items: allNews } = useContentList("news.list", contentFallback("news.list"));
  const items = allNews.slice(0, limit);
  const featured = items[0];
  const rest = items.slice(1);

  if (items.length === 0) return null;

  return (
    <section className="relative py-28 bg-[#0a0a0d]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <div className="ornament"><span>{header?.eyebrow || "Últimas Noticias"}</span></div>
            <h2 className="section-title mt-4">{header?.title || "Crónicas del Reino"}</h2>
          </div>
          <Link
            to="/news"
            className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] hover:text-[#f5c542] flex items-center gap-2 group"
          >
            Ver todas <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {featured && (
            <article className="lg:col-span-2 group relative overflow-hidden glass rounded-sm hover:translate-y-[-4px] transition-all">
              <div className="relative h-72 lg:h-[420px] overflow-hidden">
                <img src={featured.image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover saturate-50 group-hover:saturate-100 group-hover:scale-110 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0d] via-[#0a0a0d]/50 to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  {featured.pinned && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-[#d4a843] text-[#1a1408] font-display tracking-[0.2em] uppercase text-[10px] font-bold rounded-sm">
                      <Pin className="w-3 h-3" /> Pinned
                    </span>
                  )}
                  <span className="px-3 py-1 bg-[#0a0a0d]/80 backdrop-blur border border-[rgba(212,168,67,0.4)] text-[#d4a843] font-mono tracking-[0.2em] uppercase text-[10px] rounded-sm">
                    {featured.category}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a843]">{featured.date}</span>
                  <h3 className="font-display font-bold text-2xl md:text-3xl text-[#f0ebe0] mt-2 mb-3 leading-tight group-hover:text-[#f5c542] transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-sm text-[#b8b3a8] max-w-xl">{featured.excerpt}</p>
                </div>
              </div>
            </article>
          )}

          <div className="flex flex-col gap-4">
            {rest.map((n) => (
              <article key={n.id} className="group relative glass rounded-sm overflow-hidden hover:translate-y-[-3px] transition-all flex">
                <div className="relative w-32 md:w-40 flex-shrink-0 overflow-hidden">
                  <img src={n.image} alt={n.title} className="absolute inset-0 w-full h-full object-cover saturate-50 group-hover:saturate-100 transition-all duration-500" />
                </div>
                <div className="flex-1 p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-[rgba(212,168,67,0.12)] border border-[rgba(212,168,67,0.3)] text-[#d4a843] font-mono tracking-[0.2em] uppercase text-[9px] rounded-sm">
                      {n.category}
                    </span>
                    <span className="font-mono text-[10px] text-[#6f6c66]">{n.date}</span>
                  </div>
                  <h4 className="font-display font-semibold text-sm md:text-base text-[#f0ebe0] leading-snug mb-1.5 group-hover:text-[#f5c542] transition-colors line-clamp-2">
                    {n.title}
                  </h4>
                  <p className="text-xs text-[#8a8a92] line-clamp-2">{n.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default News;
